import { Grid } from "@mui/material";
import React from "react";

import {
	ShiftAllocationRules,
	ShiftAllocationRulesEmpty,
	//ShiftForceAssignment,
	ShiftScheduleRules,
	Teams,
} from "../../../../../../export/gat-admin";
import SectionWrapper from "../../../section-wrapper";
import SectionActions from "../../section-actions";
import AcceptanceCutoff from "./acceptance-cutoff";
import useShiftSettingsController from "./controllers/use-shift-settings-controller";
import FlexSignup from "./flex-signup";
import FlexiShiftSignup from "./flexi-shift-signup";
import JobExpiration from "./job-expiration/job-expiration";
import MinShiftsGap from "./min-shifts-gap";
import MinWeekHoursNotifSettings from "./min-week-hours-notif-settings";
import OverlapShifts from "./overlap-shifts";
import ShiftCancellation from "./shift-cancellation";
import ShiftPurpose from "./shift-purpose";
import ShiftSwapRules from "./shift-swap-rules";
import WorkerTypeFilter from "./worker-type-filter";

interface IShiftSettingsProps {
	locationData: any;
	creatingNewLocation: boolean;
	companySettings?: any;
	setCompanySettings?: any;
	entity: string;
}
const ShiftSettings: React.FC<IShiftSettingsProps> = ({
	locationData,
	creatingNewLocation,
	companySettings,
	setCompanySettings,
	entity,
}) => {
	const [
		{
			shiftSettings,
			newShiftSettings,
			updateCompanySettingsStatus,
			updateLocationDataStatus,
			shiftSettingsLocationData,
			newShiftSettingsLocationData,
			loading,
			overlapWorkcenters,
			newOverlapWorkcenters,
			partialShiftWorkcenters,
			newPartialShiftWorkcenters,
			flexiShiftWorkcenters,
			newFlexiShiftWorkcenters,
			acceptanceCutoffWorkcenters,
			newAcceptanceCutoffWorkcenters,
			getCompanySettingsStatus,
			getLocationSettingsStatus,
		},
		{
			setNewShiftSettings,
			handleSave,
			setOverlapWorkcenters,
			setNewOverlapWorkcenters,
			setNewShiftSettingsLocationData,
			setPartialShiftWorkcenters,
			setNewPartialShiftWorkcenters,
			setFlexiShiftWorkcenters,
			setNewFlexiShiftWorkcenters,
			setNewAcceptanceCutoffWorkcenters,
		},
	]: any = useShiftSettingsController(entity, locationData);

	const isSaving =
		loading ||
		updateCompanySettingsStatus === "pending" ||
		updateLocationDataStatus === "pending";

	const canNotSave =
		(newShiftSettings.overlappingShiftAllowed &&
			newOverlapWorkcenters.length === 0) ||
		(newShiftSettings.partialShiftAllowed &&
			newPartialShiftWorkcenters.length === 0) ||
		(newShiftSettings.flexiShiftAllowed &&
			newFlexiShiftWorkcenters.length === 0) ||
		(newShiftSettings.acceptanceCutoffEnabled &&
			newAcceptanceCutoffWorkcenters.length === 0);

	return (
		<SectionWrapper
			title='Flex Shifts Configuration'
			subtitle='Setup rules for Worker engagement on Flex/Overtime Shifts'
		>
			<Grid
				container
				sx={{
					paddingTop: "16px",
					paddingBottom: "16px",
				}}
			>
				<Grid item xs={12}>
					{entity !== "location" ? (
						<>
							<ShiftSwapRules
								newShiftSettings={newShiftSettings}
								setNewShiftSettings={setNewShiftSettings}
							/>
							<ShiftPurpose
								newShiftSettings={newShiftSettings}
								setNewShiftSettings={setNewShiftSettings}
							/>
							<ShiftCancellation
								newShiftSettings={newShiftSettings}
								setNewShiftSettings={setNewShiftSettings}
							/>
							{/* <ShiftForceAssignment
								newShiftSettings={newShiftSettings}
							// setNewShiftSettings={setNewShiftSettings}
							/> */}
							<JobExpiration
								companySettings={companySettings}
								setCompanySettings={setCompanySettings}
								newShiftSettings={newShiftSettings}
								setNewShiftSettings={setNewShiftSettings}
							/>
							<WorkerTypeFilter
								newShiftSettings={newShiftSettings}
								setNewShiftSettings={setNewShiftSettings}
							/>
						</>
					) : null}
					{!creatingNewLocation && entity === "location" ? (
						<>
							<ShiftScheduleRules locationData={locationData} />
							<Teams />
							{Object.keys(newShiftSettings).length === 0 ? (
								<ShiftAllocationRulesEmpty />
							) : (
								<ShiftAllocationRules
									locationData={newShiftSettingsLocationData}
									setLocationData={setNewShiftSettingsLocationData}
									creatingNewLocation={creatingNewLocation}
								/>
							)}
							<OverlapShifts
								overlapWorkcenters={overlapWorkcenters}
								newOverlapWorkcenters={newOverlapWorkcenters}
								setNewOverlapWorkcenters={setNewOverlapWorkcenters}
								newShiftSettings={newShiftSettings}
								setNewShiftSettings={setNewShiftSettings}
								loading={loading}
							/>
							<MinShiftsGap
								newShiftSettings={newShiftSettings}
								setNewShiftSettings={setNewShiftSettings}
								loading={loading}
							/>
							<FlexSignup
								newShiftSettings={newShiftSettings}
								setNewShiftSettings={setNewShiftSettings}
								loading={loading}
								partialShiftWorkcenters={partialShiftWorkcenters}
								newPartialShiftWorkcenters={newPartialShiftWorkcenters}
								setNewPartialShiftWorkcenters={setNewPartialShiftWorkcenters}
							/>
							<FlexiShiftSignup
								newShiftSettings={newShiftSettings}
								setNewShiftSettings={setNewShiftSettings}
								loading={loading}
								flexiShiftWorkcenters={flexiShiftWorkcenters}
								newFlexiShiftWorkcenters={newFlexiShiftWorkcenters}
								setNewFlexiShiftWorkcenters={setNewFlexiShiftWorkcenters}
							/>
							<AcceptanceCutoff
								newShiftSettings={newShiftSettings}
								setNewShiftSettings={setNewShiftSettings}
								loading={loading}
								acceptanceCutoffWorkcenters={acceptanceCutoffWorkcenters}
								newAcceptanceCutoffWorkcenters={newAcceptanceCutoffWorkcenters}
								setNewAcceptanceCutoffWorkcenters={
									setNewAcceptanceCutoffWorkcenters
								}
							/>
							<MinWeekHoursNotifSettings
								newShiftSettings={newShiftSettings}
								setNewShiftSettings={setNewShiftSettings}
							/>
						</>
					) : null}
				</Grid>
			</Grid>
			<SectionActions
				oldData={{
					...shiftSettings,
					...shiftSettingsLocationData,
					...[
						...overlapWorkcenters,
						...partialShiftWorkcenters,
						...flexiShiftWorkcenters,
						...acceptanceCutoffWorkcenters,
					],
				}}
				newData={{
					...newShiftSettings,
					...newShiftSettingsLocationData,
					...[
						...newOverlapWorkcenters,
						...newPartialShiftWorkcenters,
						...newFlexiShiftWorkcenters,
						...newAcceptanceCutoffWorkcenters,
					],
				}}
				resetChanges={() => {
					setNewShiftSettings(shiftSettings);
					setNewShiftSettingsLocationData(shiftSettingsLocationData);
					setNewOverlapWorkcenters(overlapWorkcenters);
					setNewPartialShiftWorkcenters(partialShiftWorkcenters);
					setNewFlexiShiftWorkcenters(flexiShiftWorkcenters);
					setNewAcceptanceCutoffWorkcenters(acceptanceCutoffWorkcenters);
				}}
				handleSave={handleSave}
				status={
					entity === "location"
						? getLocationSettingsStatus
						: getCompanySettingsStatus
				}
				loading={isSaving}
				canNotSave={canNotSave}
			/>
		</SectionWrapper>
	);
};

export default ShiftSettings;
